import React, { useState, useEffect, useRef } from 'react';
import { OPTIONAL_STANDARD_FIELDS, FIXED_FIELDS } from '../hooks/useApplicationForm';

const DND_CSS = `
  @keyframes fb-settle {
    0%   { transform: scale(1.03); box-shadow: 0 8px 28px rgba(108,99,255,0.28); border-color: #a78bfa; }
    100% { transform: scale(1);    box-shadow: 0 1px 4px rgba(0,0,0,0.05);      border-color: #e2e8f0; }
  }
  .fb-settle { animation: fb-settle 0.35s cubic-bezier(0.34,1.56,0.64,1) both; }
  .fb-card { transition: opacity 0.15s, transform 0.15s; }
  .fb-card.dragging { opacity: 0.4; transform: scale(0.98); }
  .fb-drag-handle { opacity: 0.25; transition: opacity 0.15s; }
  .fb-card:hover .fb-drag-handle { opacity: 0.7; }
  .fb-drag-handle:hover { opacity: 1 !important; cursor: grab; }
  .fb-drag-handle:active { cursor: grabbing; }
`;

// ── 入力形式の選択肢 ──────────────────────────────────────────────────────
const INPUT_TYPES = [
  { value: 'text',     label: 'テキスト（1行）',   icon: '✏️' },
  { value: 'textarea', label: 'テキスト（複数行）', icon: '📝' },
  { value: 'radio',    label: 'ラジオボタン',       icon: '🔘' },
  { value: 'select',   label: 'プルダウン',         icon: '▼' },
  { value: 'checkbox', label: 'チェックボックス',   icon: '☑️' },
];

const newCustomField = () => ({
  id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  type: 'custom',
  label: '',
  description: '',
  inputType: 'text',
  options: [],
  required: false,
});

// ── 6点ドットのドラッグハンドル ─────────────────────────────────────────
const DragHandle = () => (
  <div className="fb-drag-handle" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 3px)', gap: '2.5px', padding: '4px 5px', flexShrink: 0 }}>
    {Array(6).fill(0).map((_, i) => (
      <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
    ))}
  </div>
);

// ── フィールドカード（編集UI） ────────────────────────────────────────────
const FieldCard = ({
  field, onChange, onRemove, isOpen, onToggle,
  isDragging, dropPos,
  onDragStart, onDragOver, onDrop, onDragEnd,
  settled,
}) => {
  const [optionInput, setOptionInput] = useState('');
  const isFixed    = field.type === 'standard' && FIXED_FIELDS.find(f => f.id === field.id);
  const isOptStd   = field.type === 'standard' && !isFixed;
  const hasOptions = ['radio', 'select', 'checkbox'].includes(field.inputType);

  return (
    <div
      draggable={!isFixed}
      onDragStart={!isFixed ? () => onDragStart(field.id) : undefined}
      onDragOver={!isFixed ? (e) => onDragOver(e, field.id) : undefined}
      onDrop={!isFixed ? (e) => { e.preventDefault(); onDrop(field.id); } : undefined}
      onDragEnd={onDragEnd}
      className={`fb-card${isDragging ? ' dragging' : ''}${settled ? ' fb-settle' : ''}`}
      style={{
        background: '#fff',
        border: `1.5px solid ${isFixed ? '#c4b5fd' : '#e2e8f0'}`,
        borderRadius: 12,
        overflow: 'visible',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* ドロップインジケーター */}
      {dropPos === 'before' && <div style={{ position: 'absolute', top: -2, left: 8, right: 8, height: 3, borderRadius: 2, background: '#6c63ff', boxShadow: '0 0 6px rgba(108,99,255,0.6)', zIndex: 10 }} />}
      {dropPos === 'after'  && <div style={{ position: 'absolute', bottom: -2, left: 8, right: 8, height: 3, borderRadius: 2, background: '#6c63ff', boxShadow: '0 0 6px rgba(108,99,255,0.6)', zIndex: 10 }} />}

      {/* ヘッダー行 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px 10px 6px',
        background: isFixed ? '#f5f3ff' : '#fafafa',
        borderRadius: '10px 10px 0 0',
        cursor: isFixed ? 'default' : 'pointer',
        userSelect: 'none',
      }}
        onClick={() => !isFixed && onToggle(field.id)}
      >
        {/* ドラッグハンドル */}
        {!isFixed && <DragHandle />}

        {/* ラベル */}
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
          {field.label || '（タイトル未設定）'}
          {field.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
        </span>

        {/* バッジ */}
        {isFixed   && <span style={{ fontSize: 10, fontWeight: 700, color: '#6c63ff', background: '#ede9fe', padding: '2px 8px', borderRadius: 20 }}>必須固定</span>}
        {isOptStd  && <span style={{ fontSize: 10, fontWeight: 700, color: '#0284c7', background: '#e0f2fe', padding: '2px 8px', borderRadius: 20 }}>規定</span>}
        {!isFixed && !isOptStd && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 20 }}>
            {INPUT_TYPES.find(t => t.value === field.inputType)?.label}
          </span>
        )}

        {/* 削除 */}
        {!isFixed && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(field.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#f87171', lineHeight: 1, padding: '0 4px' }}>×</button>
        )}

        {/* 展開 */}
        {!isFixed && (
          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 2 }}>{isOpen ? '▾' : '▸'}</span>
        )}
      </div>

      {/* 編集パネル */}
      {!isFixed && isOpen && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid #f1f5f9' }}>

          {/* タイトル */}
          <div>
            <label style={labelStyle}>タイトル</label>
            <input
              value={field.label}
              onChange={e => onChange(field.id, { label: e.target.value })}
              placeholder="例：好きなVRChatワールドは？"
              style={inputStyle}
            />
          </div>

          {/* 説明文 */}
          <div>
            <label style={labelStyle}>説明文 <span style={{ fontWeight: 400, color: '#94a3b8' }}>（任意）</span></label>
            <input
              value={field.description}
              onChange={e => onChange(field.id, { description: e.target.value })}
              placeholder="入力のヒントや補足説明"
              style={inputStyle}
            />
          </div>

          {/* 入力形式（カスタムのみ） */}
          {field.type === 'custom' && (
            <div>
              <label style={labelStyle}>入力形式</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {INPUT_TYPES.map(t => (
                  <button key={t.value}
                    onClick={() => onChange(field.id, { inputType: t.value, options: [] })}
                    style={{
                      padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1.5px solid',
                      borderColor: field.inputType === t.value ? '#6c63ff' : '#e2e8f0',
                      background:  field.inputType === t.value ? '#f5f3ff' : '#fff',
                      color:       field.inputType === t.value ? '#6c63ff' : '#64748b',
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 選択肢（radio/select/checkbox） */}
          {field.type === 'custom' && hasOptions && (
            <div>
              <label style={labelStyle}>選択肢</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(field.options || []).map((opt, oi) => (
                  <div key={oi} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={opt}
                      onChange={e => {
                        const opts = [...field.options];
                        opts[oi] = e.target.value;
                        onChange(field.id, { options: opts });
                      }}
                      style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                    />
                    <button onClick={() => onChange(field.id, { options: field.options.filter((_, i) => i !== oi) })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 16 }}>×</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={optionInput} onChange={e => setOptionInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && optionInput.trim()) {
                        onChange(field.id, { options: [...(field.options || []), optionInput.trim()] });
                        setOptionInput('');
                      }
                    }}
                    placeholder="選択肢を入力して Enter"
                    style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  />
                  <button onClick={() => {
                    if (!optionInput.trim()) return;
                    onChange(field.id, { options: [...(field.options || []), optionInput.trim()] });
                    setOptionInput('');
                  }}
                    style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#6c63ff', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    追加
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 必須設定 */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={field.required}
              onChange={e => onChange(field.id, { required: e.target.checked })}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            この項目を必須にする
          </label>
        </div>
      )}
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5, letterSpacing: '0.5px' };
const inputStyle  = { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 0 };

// ── FormBuilder メインコンポーネント ─────────────────────────────────────
const FormBuilder = ({ formDef, onSave, onClose }) => {
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // ── DnD state ──────────────────────────────────────────────────────────
  const [dragId, setDragId]       = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverPos, setDragOverPos] = useState(null); // 'before' | 'after'
  const [settledId, setSettledId] = useState(null);
  const dragPosRef = useRef(null);

  const handleDragStart = (id) => setDragId(id);

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id === dragId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    setDragOverId(id);
    setDragOverPos(pos);
    dragPosRef.current = pos;
  };

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setDragOverId(null); return; }
    const from = fields.findIndex(f => f.id === dragId);
    const to   = fields.findIndex(f => f.id === targetId);
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    const insertAt = dragPosRef.current === 'before' ? to : to + 1;
    next.splice(insertAt > from ? insertAt - 1 : insertAt, 0, moved);
    setFields(next);
    setSettledId(dragId);
    setTimeout(() => setSettledId(null), 400);
    setDragId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };
  // ───────────────────────────────────────────────────────────────────────

  const handleToggle = (id) => setExpandedId(prev => prev === id ? null : id);

  useEffect(() => {
    const fixedIds = new Set(FIXED_FIELDS.map(f => f.id));
    const saved = formDef?.fields || [];
    setFields(saved.filter(f => !fixedIds.has(f.id)));
    setTitle(formDef?.title ?? '');
    setDescription(formDef?.description ?? '');
  }, [formDef]);

  const handleChange = (id, updates) =>
    setFields(fs => fs.map(f => f.id === id ? { ...f, ...updates } : f));

  const handleRemove = (id) =>
    setFields(fs => fs.filter(f => f.id !== id));


  const addCustomField = () => {
    const f = { ...newCustomField(), label: '' };
    setFields(fs => [...fs, f]);
    setExpandedId(f.id);
  };

  const addStandardField = (stdField) => {
    if (fields.find(f => f.id === stdField.id)) return;
    setFields(fs => [...fs, { ...stdField, type: 'standard', required: false, description: '' }]);
    setExpandedId(stdField.id);
  };

  const handleSave = async () => {
    setSaving(true);
    const allFields = [...FIXED_FIELDS, ...fields];
    await onSave(allFields, title.trim(), description.trim());
    setSaving(false);
    onClose();
  };

  // 追加可能な規定フィールド（未追加のもの）
  const addableStd = OPTIONAL_STANDARD_FIELDS.filter(sf => !fields.find(f => f.id === sf.id));

  const customCount = fields.filter(f => f.type === 'custom').length;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: '#f8fafc', borderRadius: 20,
        width: '100%', maxWidth: 960,
        height: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
        overflow: 'hidden',
      }}>
        <style>{DND_CSS}</style>

        {/* ヘッダー */}
        <div style={{ padding: '18px 24px 14px', background: '#fff', borderBottom: '1px solid #e8edf5', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#6c63ff', letterSpacing: 1.5, textTransform: 'uppercase' }}>応募フォーム</p>
              <h2 style={{ margin: '3px 0 0', fontSize: 20, fontWeight: 900, color: '#0f172a' }}>フォームを編集</h2>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* 2カラム本体 */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* 左: 固定フィールド（スクロールなし） */}
          <div style={{ width: 280, flexShrink: 0, minHeight: 0, borderRight: '1px solid #e8edf5', background: '#fff', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
            {/* フォーム設定 */}
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1.5, textTransform: 'uppercase' }}>フォーム設定</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>フォームタイトル</label>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="例：6月イベント 応募フォーム"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>説明文 <span style={{ fontWeight: 400, color: '#94a3b8' }}>（任意）</span></label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="例：6月15日開催のイベントです。定員20名。締め切りは6月10日。"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                />
              </div>
            </div>

            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1.5, textTransform: 'uppercase' }}>必須項目（変更不可）</p>
            {FIXED_FIELDS.map((f) => (
              <FieldCard key={f.id} field={f}
                onChange={() => {}} onRemove={() => {}}
                isOpen={false} onToggle={() => {}}
                isDragging={false} dropPos={null}
                onDragStart={() => {}} onDragOver={() => {}} onDrop={() => {}} onDragEnd={() => {}}
                settled={false} />
            ))}
            <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, background: '#f5f3ff', border: '1px solid #c4b5fd', fontSize: 12, color: '#5b21b6', lineHeight: 1.6 }}>
              💡 VRChat表示名とプロフィールURLは全フォームで必須です
            </div>
          </div>

          {/* 右: 3段構成（上固定・中スクロール・下固定） */}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

            {/* 上: 追加ボタン群（常時表示） */}
            <div style={{ flexShrink: 0, padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#475569' }}>項目を追加</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {addableStd.map(sf => (
                  <button key={sf.id} onClick={() => addStandardField(sf)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #0ea5e9', background: '#f0f9ff', color: '#0284c7', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    + {sf.label}
                  </button>
                ))}
                <button onClick={addCustomField}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #6c63ff', background: '#f5f3ff', color: '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  + 自由項目を追加
                </button>
              </div>
            </div>

            {/* 中: 追加済みフィールド（スクロール） */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'scroll', padding: '16px 20px' }}>
              {fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <span style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>➕</span>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>上のボタンで項目を追加してください</p>
                </div>
              ) : (
                <>
                  <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1.5, textTransform: 'uppercase' }}>追加項目 ({fields.length})</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {fields.map((f) => (
                      <FieldCard key={f.id} field={f}
                        onChange={handleChange} onRemove={handleRemove}
                        isOpen={expandedId === f.id} onToggle={handleToggle}
                        isDragging={dragId === f.id}
                        dropPos={dragOverId === f.id ? dragOverPos : null}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                        settled={settledId === f.id} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* 下: バリデーション警告（常時表示） */}
            {customCount > 0 && fields.filter(f => f.type === 'custom' && !f.label.trim()).length > 0 && (
              <div style={{ flexShrink: 0, padding: '10px 20px', borderTop: '1px solid #fde68a', background: '#fffbeb', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                ⚠️ タイトルが未設定のカスタム項目があります
              </div>
            )}
          </div>
        </div>

        {/* フッター */}
        <div style={{ padding: '14px 24px', background: '#fff', borderTop: '1px solid #e8edf5', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            キャンセル
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: saving ? '#c4b5fd' : '#6c63ff', color: '#fff', fontSize: 13, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(108,99,255,0.3)' }}>
            {saving ? '保存中...' : '💾 保存する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
